/**
 * LabelControls Component
 *
 * Renders original and corrected labels for audit decisions
 */

import type { Label } from '@internal-audit/domain';
import type { GraphQLAuditDecision } from '@internal-audit/domain/graphql-types';
import { Text, Badge, BadgeSize, BadgeVariant } from '@soteria/design-system-ve';

import * as styles from './AuditDecisionColumns.css';

interface LabelControlsProps {
  decision: GraphQLAuditDecision;
  originalClassificationResults?: Array<{
    name: string;
    severity: number;
  }>;
}

function findLabelChanges(original: Label[], corrected: Label[]) {
  const changes: Array<{
    type: 'removed' | 'added' | 'modified' | 'unchanged';
    original?: Label;
    corrected?: Label;
  }> = [];

  // Create a set to track which corrected labels we've already processed
  const processedCorrectedLabels = new Set<string>();

  // If original labels are empty, treat all corrected labels as unchanged
  // This handles existing decisions that were created before the BFF fix
  if (original.length === 0 && corrected.length > 0) {
    corrected.forEach(corrLabel => {
      changes.push({ type: 'unchanged', corrected: corrLabel });
    });

    return changes;
  }

  // Process original labels first
  original.forEach(origLabel => {
    const found = corrected.find(corrLabel => {
      const nameMatch = corrLabel.name === origLabel.name;
      const severityMatch = corrLabel.severity === origLabel.severity;

      return nameMatch && severityMatch;
    });

    if (!found) {
      // Check if there's a label with same name but different severity
      const modifiedLabel = corrected.find(corrLabel => corrLabel.name === origLabel.name);

      if (modifiedLabel) {
        // For SAFE labels, treat severity changes as unchanged since we don't display severity
        if (origLabel.name.toLowerCase() === 'safe' && modifiedLabel.name.toLowerCase() === 'safe') {
          changes.push({ type: 'unchanged', original: origLabel, corrected: modifiedLabel });
        } else {
          changes.push({ type: 'modified', original: origLabel, corrected: modifiedLabel });
        }

        processedCorrectedLabels.add(`${modifiedLabel.name}-${modifiedLabel.severity}`);
      } else {
        changes.push({ type: 'removed', original: origLabel });
      }
    } else {
      // Label exists in both with same name and severity - unchanged
      changes.push({ type: 'unchanged', original: origLabel, corrected: found });
      processedCorrectedLabels.add(`${found.name}-${found.severity}`);
    }
  });

  // Find truly added labels (only those not processed above)
  corrected.forEach(corrLabel => {
    const labelKey = `${corrLabel.name}-${corrLabel.severity}`;

    if (!processedCorrectedLabels.has(labelKey)) {
      changes.push({ type: 'added', corrected: corrLabel });
    }
  });

  return changes;
}

// Helper function to format label display text
function formatLabelDisplay(name: string, severity: number): { text: string; title: string } {
  if (name.toLowerCase() === 'safe') {
    return {
      text: name.toUpperCase(),
      title: name,
    };
  }

  return {
    text: `${name.toUpperCase()} ${severity}`,
    title: `${name} (${severity})`,
  };
}

// Component for removed label badge
function RemovedLabelBadge({ label }: { label: Label }) {
  const { text, title } = formatLabelDisplay(label.name, label.severity);

  return <Badge className={styles.removedBadge} size={BadgeSize.Small} text={`- ${text}`} title={`Removed: ${title}`} variant={BadgeVariant.Error} />;
}

// Component for added label badge
function AddedLabelBadge({ label }: { label: Label }) {
  const { text, title } = formatLabelDisplay(label.name, label.severity);

  return <Badge className={styles.addedBadge} size={BadgeSize.Small} text={`+ ${text}`} title={`Added: ${title}`} variant={BadgeVariant.Success} />;
}

// Component for modified label badge
function ModifiedLabelBadge({ original, corrected }: { original: Label; corrected: Label }) {
  const originalDisplay = formatLabelDisplay(original.name, original.severity);
  const correctedDisplay = formatLabelDisplay(corrected.name, corrected.severity);

  // For SAFE labels, don't show the arrow if both are SAFE (since we don't show severity)
  if (original.name.toLowerCase() === 'safe' && corrected.name.toLowerCase() === 'safe') {
    return (
      <Badge
        className={styles.modifiedBadge}
        size={BadgeSize.Small}
        text={correctedDisplay.text}
        title={`Modified: ${original.name} (${original.severity} → ${corrected.severity})`}
        variant={BadgeVariant.Warning}
      />
    );
  }

  return (
    <Badge
      className={styles.modifiedBadge}
      size={BadgeSize.Small}
      text={`${originalDisplay.text} → ${correctedDisplay.text}`}
      title={`Modified: ${originalDisplay.title} → ${correctedDisplay.title}`}
      variant={BadgeVariant.Warning}
    />
  );
}

// Component for unchanged label badge
function UnchangedLabelBadge({ label }: { label: Label }) {
  const { text, title } = formatLabelDisplay(label.name, label.severity);

  return <Badge size={BadgeSize.Small} text={text} title={title} />;
}

// Helper function to render a label change badge
function renderLabelChangeBadge(change: { type: 'removed' | 'added' | 'modified' | 'unchanged'; original?: Label; corrected?: Label }, index: number) {
  const key = `${change.type}-${index}`;

  switch (change.type) {
    case 'removed':
      return change.original ? <RemovedLabelBadge key={key} label={change.original} /> : null;
    case 'added':
      return change.corrected ? <AddedLabelBadge key={key} label={change.corrected} /> : null;
    case 'modified':
      return change.original && change.corrected ? <ModifiedLabelBadge key={key} corrected={change.corrected} original={change.original} /> : null;
    case 'unchanged':
      return change.corrected ? <UnchangedLabelBadge key={key} label={change.corrected} /> : null;
    default:
      return null;
  }
}

export function LabelControls({ decision, originalClassificationResults }: LabelControlsProps) {
  // Use original classification results as the source of truth for original labels
  const originalLabels =
    originalClassificationResults?.map(result => ({
      name: result.name,
      severity: result.severity,
    })) || [];

  const correctedLabels = decision.correctedLabels || [];

  const hasOriginalLabels = originalLabels.length > 0;
  const hasCorrectedLabels = correctedLabels.length > 0;

  if (!hasOriginalLabels && !hasCorrectedLabels) {
    return (
      <div className={styles.labelControlsContainer}>
        <div className={styles.labelDecisionsContainer}>
          <Text>No labels</Text>
        </div>
      </div>
    );
  }

  // If there are no corrected labels, just display the original labels with default styling
  if (!hasCorrectedLabels && hasOriginalLabels) {
    return (
      <div className={styles.labelControlsContainer}>
        <div className={styles.labelDecisionsContainer}>
          {originalLabels.map(label => (
            <UnchangedLabelBadge key={`${label.name}-${label.severity}`} label={label} />
          ))}
        </div>
      </div>
    );
  }

  const changes = findLabelChanges(originalLabels, correctedLabels);

  return (
    <div className={styles.labelControlsContainer}>
      <div className={styles.labelDecisionsContainer}>{changes.map(renderLabelChangeBadge)}</div>
    </div>
  );
}
